import { readFromLocalStorage } from '@/services/localStorage'

const ALGERIA_CURRENCY_CODE = 'DZD'
const ALGERIA_CALLING_CODE_DIGITS = '213'

const isAlgeriaCurrency = (currency: string) =>
  currency.trim().toUpperCase() === ALGERIA_CURRENCY_CODE

export const isAlgeriaCallingCode = (
  countryCode: string | null | undefined
): boolean => {
  if (countryCode == null || countryCode === '') return false
  const digits = countryCode.replace(/\D/g, '').replace(/^0+/, '')
  return digits === ALGERIA_CALLING_CODE_DIGITS
}

const getAlgeriaFractionDigits = (value: number) => {
  const roundedCents = Math.round(value * 100)
  const hasFractionalUnits = Math.abs(roundedCents) % 100 !== 0

  return hasFractionalUnits
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { minimumFractionDigits: 0, maximumFractionDigits: 0 }
}

export type FormatPriceOptions = {
  signDisplay?: 'never' | 'always' | 'auto'
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  /**
   * Store profile primary phone `country_code` (e.g. "+213"). Prefer passing from Redux
   * (`selectPhoneCode` / store slice) in hot lists to avoid storage reads. When omitted,
   * `phoneCode` is read from localStorage with parse **cached** until the stored string changes.
   */
  phoneCountryCode?: string | null
}

const PHONE_CODE_CACHE_UNSET = Symbol('phoneCodeCacheUnset')

let phoneCodeCacheRaw: string | null | typeof PHONE_CODE_CACHE_UNSET =
  PHONE_CODE_CACHE_UNSET
let phoneCodeCacheParsed: string | null = null

const readStorePhoneCodeCached = (): string | null => {
  let raw: string | null
  try {
    raw = localStorage.getItem('phoneCode')
  } catch {
    return readFromLocalStorage('phoneCode')
  }

  if (
    phoneCodeCacheRaw !== PHONE_CODE_CACHE_UNSET &&
    raw === phoneCodeCacheRaw
  ) {
    return phoneCodeCacheParsed
  }

  phoneCodeCacheRaw = raw
  phoneCodeCacheParsed = readFromLocalStorage('phoneCode')
  return phoneCodeCacheParsed
}

/** Clears cached `phoneCode` (e.g. in tests after mutating localStorage outside `setItem`). */
export const resetFormatPricePhoneCodeCache = (): void => {
  phoneCodeCacheRaw = PHONE_CODE_CACHE_UNSET
  phoneCodeCacheParsed = null
}

const resolveStorePhoneCountryCode = (
  options?: FormatPriceOptions
): string | null => {
  if (options?.phoneCountryCode !== undefined) {
    return options.phoneCountryCode
  }
  return readStorePhoneCodeCached()
}

const omitFractionDigitOptions = (options?: FormatPriceOptions) => {
  if (!options) return {}
  const {
    minimumFractionDigits: _min,
    maximumFractionDigits: _max,
    phoneCountryCode: _phone,
    ...rest
  } = options
  return rest
}

/**
 * Intl throws RangeError when minimumFractionDigits > maximumFractionDigits.
 * Callers can pass partial options that conflict with defaults after spread.
 */
const normalizeIntlFractionDigits = (
  opts: Intl.NumberFormatOptions
): Intl.NumberFormatOptions => {
  const { minimumFractionDigits: min, maximumFractionDigits: max } = opts
  if (typeof min === 'number' && typeof max === 'number' && min > max) {
    return { ...opts, minimumFractionDigits: max, maximumFractionDigits: max }
  }
  return opts
}

export const formatPrice = (
  value: number,
  currency: string,
  options?: FormatPriceOptions
) => {
  const useAlgeriaTrailingDecimalRules =
    (isAlgeriaCurrency(currency) ||
      isAlgeriaCallingCode(resolveStorePhoneCountryCode(options))) &&
    options?.maximumFractionDigits !== 0

  const numberFormatOptions = normalizeIntlFractionDigits({
    style: 'currency',
    signDisplay: 'exceptZero',
    currencyDisplay: 'narrowSymbol',
    currency,
    ...(useAlgeriaTrailingDecimalRules
      ? {
          ...getAlgeriaFractionDigits(value),
          ...omitFractionDigitOptions(options)
        }
      : {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          ...options
        })
  })

  try {
    const formatter = new Intl.NumberFormat('fr-FR', numberFormatOptions)

    return formatter.format(value).replaceAll(',', '.')
  } catch (error) {
    console.warn(`Invalid currency code: ${currency}. Falling back to 'USD'.`)

    const fallbackFormatter = new Intl.NumberFormat('fr-FR', {
      ...normalizeIntlFractionDigits({
        ...numberFormatOptions,
        currency: 'USD'
      })
    })

    return fallbackFormatter.format(value).replaceAll(',', '.')
  }
}
